/**
 * Sticky add-to-cart bar for the PDP.
 *
 * Bidirectional variant sync between the sticky <select> and the main
 * <variant-selects> picker, built on DOM CustomEvents:
 *
 *  - Sticky → main: on `change`, <sticky-variant-select> dispatches a bubbling
 *    'sticky-variant:change' CustomEvent carrying { sectionId, variantId,
 *    optionValues }. A document-level listener (defined below) propagates the
 *    new option values onto the matching <variant-selects>'s radios /
 *    dropdowns and dispatches a `change` on the affected input so the existing
 *    VariantSelects pipeline runs (publishes optionValueSelectionChange →
 *    product-info refetches the section → publishes variantChange).
 *
 *  - Main → sticky: subscribes to PUB_SUB_EVENTS.variantChange (the existing
 *    pubsub signal published by product-info.js once a variant fetch settles)
 *    and updates the sticky <select>.value + the hidden id inputs to match the
 *    resolved variant. Programmatic `select.value = …` does NOT fire `change`,
 *    so this can never loop back through the sticky→main path.
 *
 * Each sticky <option> is rendered with data-option-1 / data-option-2 /
 * data-option-3 (variant.option1/2/3) by snippets/product-sticky-add-cart.liquid
 * — that is the bridge that lets us go variant id → main-picker option values.
 */

const STICKY_VARIANT_CHANGE_EVENT = 'sticky-variant:change'

class StickyProVariantSelect extends HTMLElement {
  constructor() {
    super()
    this.boundOnStickyChange = this.onStickyChange.bind(this)
    this.boundOnVariantChange = this.onMainVariantChange.bind(this)
    this.boundOnStickyQtyChange = this.onStickyQtyChange.bind(this)
    this.boundOnMainQtyChange = this.onMainQtyChange.bind(this)
  }

  connectedCallback() {
    this.addEventListener('change', this.boundOnStickyChange)
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.boundOnVariantChange
      )
    }

    this.stickyQtyInput?.addEventListener('change', this.boundOnStickyQtyChange)
    this.mainQtyInput?.addEventListener('change', this.boundOnMainQtyChange)

    // Set the sticky ATC button to match the initially selected variant
    // (covers multi-variant products that load on a sold-out variant).
    this.updateStickyAtcState()

    // Keep the sticky qty in sync when product-info updates the main qty via
    // pubsub (e.g. after a variant change) rather than a DOM change event.
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      this.quantityUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.quantityUpdate,
        this.onQuantityUpdate.bind(this)
      )
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.boundOnStickyChange)
    this.variantChangeUnsubscriber?.()
    this.variantChangeUnsubscriber = undefined

    this.stickyQtyInput?.removeEventListener('change', this.boundOnStickyQtyChange)
    this.mainQtyInput?.removeEventListener('change', this.boundOnMainQtyChange)

    this.quantityUpdateUnsubscriber?.()
    this.quantityUpdateUnsubscriber = undefined
  }

  get sectionId() {
    return this.dataset.section
  }

  get select() {
    return this.querySelector('select')
  }

  get stickyAtcButton() {
    return document.querySelector(
      `#product-sticky-form-${this.sectionId} .sticky__atc-button`
    )
  }

  get stickyQtyInput() {
    return document.querySelector(`#Sticky-Quantity-${this.sectionId}`)
  }

  get mainQtyInput() {
    return document.querySelector(`#Quantity-${this.sectionId}`)
  }

  // Reflect the selected variant's availability on the sticky ATC button, e.g.
  // a product that loads on a sold-out variant. Reads the selected option's
  // `data-available` and toggles the button's disabled state + label.
  updateStickyAtcState() {
    const select = this.select
    const button = this.stickyAtcButton
    if (!select || !button) return

    const selectedOption = select.options[select.selectedIndex]
    const isAvailable = selectedOption?.dataset.available !== 'false'

    button.disabled = !isAvailable

    const label = button.querySelector('.sticky__atc-label')
    if (label && typeof window.variantStrings !== 'undefined') {
      label.textContent = isAvailable
        ? window.variantStrings.addToCart
        : window.variantStrings.soldOut
    }
  }

  // Keep the sticky form's hidden id input and the legacy global hidden input
  // in lockstep with `variantId`.
  syncStickyHiddenInputs(variantId) {
    const value = variantId != null ? String(variantId) : ''

    const productForm = document.querySelector(
      `#product-sticky-form-${this.sectionId}`
    )
    const formIdInput = productForm?.querySelector('input[name="id"]')
    if (formIdInput) formIdInput.value = value

    const stickySelectedInput = document.querySelector(
      '#sticky__selected_variant_id'
    )
    if (stickySelectedInput) stickySelectedInput.value = value
  }

  // Main → sticky.
  onMainVariantChange(payload) {
    const data = payload?.data
    if (!data) return
    // Ignore events from other sections (e.g. featured-product on a PDP).
    if (data.sectionId && this.sectionId && data.sectionId !== this.sectionId) {
      return
    }

    const select = this.select
    const variantId = data.variant?.id != null ? String(data.variant.id) : ''

    if (select && variantId && select.value !== variantId) {
      // Programmatic value change does NOT fire `change`, so no loop back.
      // Unavailable variants are non-selectable in the main picker, so the
      // resolved id always matches a selectable (non-disabled) sticky option.
      select.value = variantId
    }
    this.syncStickyHiddenInputs(variantId)
    this.updateStickyAtcState()
  }

  // Sticky → main: emit a CustomEvent. The document-level bridge below applies
  // the change to the main picker.
  onStickyChange() {
    const select = this.select
    if (!select) return

    this.syncStickyHiddenInputs(select.value)
    this.updateStickyAtcState()

    const selectedOption = select.options[select.selectedIndex]
    if (!selectedOption) return

    const optionValues = []
    for (let i = 1; i <= 3; i++) {
      const v = selectedOption.dataset[`option-${i}`]
      if (v != null && v !== '') optionValues.push(v)
    }
    if (!optionValues.length) return

    this.dispatchEvent(
      new CustomEvent(STICKY_VARIANT_CHANGE_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          sectionId: this.sectionId,
          variantId: select.value,
          optionValues,
        },
      })
    )
  }

  // Sticky quantity → main quantity.
  onStickyQtyChange() {
    const sticky = this.stickyQtyInput
    const main = this.mainQtyInput
    if (!sticky || !main || main.value === sticky.value) return
    main.value = sticky.value
    main.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Main quantity → sticky quantity.
  onMainQtyChange() {
    const sticky = this.stickyQtyInput
    const main = this.mainQtyInput
    if (!sticky || !main || sticky.value === main.value) return
    sticky.value = main.value
    sticky.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Catches pubsub-driven quantity updates (e.g. after variant change via
  // setQuantityBoundries) which don't fire a DOM change event on the input.
  onQuantityUpdate() {
    const sticky = this.stickyQtyInput
    const main = this.mainQtyInput
    if (!sticky || !main || sticky.value === main.value) return
    sticky.value = main.value
    sticky.dispatchEvent(new Event('change', { bubbles: true }))
  }
}
customElements.define('sticky-variant-select', StickyProVariantSelect)

/**
 * Document-level bridge: walk the matching <variant-selects> and apply the
 * sticky's chosen variant to it. Lives at the document level (not on the
 * custom element) so it survives section/product swaps and is easy to unit
 * test or replace.
 */
document.addEventListener(STICKY_VARIANT_CHANGE_EVENT, (event) => {
  const detail = event.detail || {}
  const { sectionId, optionValues } = detail
  if (!sectionId || !optionValues || !optionValues.length) return

  const variantSelects = document.querySelector(
    `variant-selects[data-section="${sectionId}"]`
  )
  if (!variantSelects) return

  // Each direct .product-form__input child of <variant-selects> is one
  // product option (swatch fieldset, button fieldset, or dropdown div),
  // rendered in product.options_with_values order. Iterate via children to
  // avoid `:scope >` quirks across older browsers.
  const optionContainers = Array.from(variantSelects.children).filter(
    (child) => child.classList && child.classList.contains('product-form__input')
  )

  let lastChangedInput = null

  optionValues.forEach((value, idx) => {
    const container = optionContainers[idx]
    if (!container) return

    // Radio picker (swatch / button / pill). `.click()` runs the full radio
    // activation algorithm (auto-unchecks siblings, fires `input` + `change`),
    // which is more robust than setting `.checked = true` and dispatching a
    // synthetic change ourselves.
    const radios = Array.from(container.querySelectorAll('input[type="radio"]'))
    if (radios.length) {
      const match = radios.find((r) => r.value === value)
      if (match && !match.checked) {
        match.click()
        lastChangedInput = match // already dispatched its own change event
      }
      return
    }

    // Dropdown picker. Setting `.value` programmatically does NOT fire change;
    // we dispatch one bubbling change at the end so VariantSelects' handler
    // runs the same code path it does for user clicks.
    const dropdown = container.querySelector('select')
    if (dropdown && dropdown.value !== value) {
      dropdown.value = value
      lastChangedInput = dropdown
    }
  })

  // For the dropdown path, fire a single bubbling change so VariantSelects
  // publishes optionValueSelectionChange and product-info refetches the
  // section. (Radio path already fired its own change via .click().)
  if (lastChangedInput && lastChangedInput.tagName === 'SELECT') {
    lastChangedInput.dispatchEvent(new Event('change', { bubbles: true }))
  }
})

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    stickyScroll()
  })
  document.addEventListener('shopify:section:select', (event) => {
    stickyScroll()
  })
  document.addEventListener('shopify:section:deselect', (event) => {
    stickyScroll()
  })
}

const stickyScroll = () => {
  let stickySelectedVariant = document.querySelector(`#sticky__variant`)
  if (stickySelectedVariant) {
    let stickySelectVairantInput = document.querySelector(
      '#sticky__selected_variant_id'
    )
    stickySelectVairantInput.value = stickySelectedVariant.value
  }

  const BuyButtonForm = document.querySelector('.product_buy_button_form')
  const productStickyWrapper = document.querySelector('.product__sticky')

  window.addEventListener('scroll', function () {
    document.documentElement.style.setProperty(
      '--sticky-bar-height',
      `${productStickyWrapper.clientHeight}px`
    )
    let BuyBUttonOffset = TopOffset(BuyButtonForm)
    let BuyBUttonTopffset = BuyBUttonOffset.top

    if (window.scrollY > BuyBUttonTopffset) {
      productStickyWrapper.classList.add('sticky')
      document.body.classList.add('sticky__cart')
    } else {
      productStickyWrapper.classList.remove('sticky')
      document.body.classList.remove('sticky__cart')
    }
  })
}
stickyScroll()
